import type { ProductCode } from 'chery-contracts';

/** Page 03 — products, launch calendar, channel mix, market insights, retail network (static copy; no image slots). */
export const PRODUCTS_INTRO = {
  eyebrow: 'From the Board · Product Plan · Page 03',
  title: 'Our products — Q4 2026 → 2027',
  body: 'A national relaunch in Q4 2026, then a launch every quarter through 2027 — an 11-model portfolio across six segments. Select a model to open its page.',
} as const;

export interface Launch {
  when: string;
  what: string;
  note: string;
  /** The relaunch itself, set apart from the 2027 launches. */
  lead?: boolean;
}

export const LAUNCH_PROGRAM_TITLE =
  'Launch program — every launch gets PR, media test drives, influencer activation, digital, showroom and fleet previews';

export const LAUNCHES: readonly Launch[] = [
  { when: 'Q4 2026 · Phase 1', what: 'National relaunch', note: 'Tiggo 9 & 7 PHEV, Tiggo 4 HEV, Himla', lead: true },
  { when: 'Q1 2027', what: 'Tiggo V', note: 'Lifestyle SUV · 105 units' },
  { when: 'Q2 2027', what: 'QQ3 EV', note: 'Market disruptor · 351 units' },
  { when: 'Q3 2027', what: 'Tiggo X4', note: 'Urban SUV · 106 units' },
  { when: 'Q3 2027', what: 'Tiggo X7', note: 'Core family SUV · 175 units' },
  { when: 'Q4 2027', what: 'Tiggo X9', note: 'Premium flagship · 47 units' },
];

export const PLAN_2027_TITLE = '2027 plan — 1,473 units · 30.8M JOD revenue · 15 variants';

export interface ProductModel {
  id: string;
  /** The stable product code (Phase 8) its live figures and `product_metric_bindings` rows are keyed by. */
  code: ProductCode;
  model: string;
  role: string;
  segment: string;
  /** Planned units, 2027. */
  units: number;
  launch: string;
  intro: string;
}

export const MODELS: readonly ProductModel[] = [
  {
    id: 'qq3',
    code: 'QQ3-EV',
    model: 'QQ3 EV',
    role: 'Market disruptor',
    segment: 'EV',
    units: 319,
    launch: 'Launches Q2 2027',
    intro:
      'The market disruptor: a price-led city EV that alone carries one sale in five of the whole plan. Its job is conquest — pulling buyers out of used imports and into the showroom, where every other line benefits from the footfall.',
  },
  {
    id: 'ar5',
    code: 'ARRIZO-5',
    model: 'Arrizo 5',
    role: 'Volume builder',
    segment: 'Sedan',
    units: 286,
    launch: 'Available from relaunch',
    intro:
      'The workhorse sedan and second-largest line. Value-for-money retail plus a natural fleet pick for ride-hailing, delivery and rental — steady volume in every month of the plan.',
  },
  {
    id: 'x7',
    code: 'TIGGO-X7',
    model: 'Tiggo X7',
    role: 'Core family SUV',
    segment: 'SUV',
    units: 159,
    launch: 'Launches Q3 2027',
    intro:
      'The core family SUV of the X-Series generation, timed to arrive with the flagship showroom. Aimed squarely at the heart of Jordan’s SUV market — the volume anchor of the second half.',
  },
  {
    id: 'ar8',
    code: 'ARRIZO-8',
    model: 'Arrizo 8',
    role: 'Executive sedan',
    segment: 'Sedan',
    units: 129,
    launch: 'Available 2027',
    intro:
      'The executive sedan: corporate fleets, banks and premium retail buyers who want size and refinement without a European price. Carries the brand upmarket alongside the X-Series.',
  },
  {
    id: 't4',
    code: 'TIGGO-4-HEV',
    model: 'Tiggo 4 HEV',
    role: 'Retail hero',
    segment: 'HEV',
    units: 127,
    launch: 'Q4 2026 relaunch line',
    intro:
      'The retail hero of the relaunch: an affordable hybrid SUV that lets first-time Chery buyers say yes. Front line of the Q4 2026 national relaunch alongside the Tiggo 9, 7 PHEV and Himla.',
  },
  {
    id: 'x4',
    code: 'TIGGO-X4',
    model: 'Tiggo X4',
    role: 'Urban SUV',
    segment: 'SUV',
    units: 96,
    launch: 'Launches Q3 2027',
    intro:
      'The urban compact SUV of the X-Series — city-sized, tech-forward, priced under the X7. Together the two Q3 launches renew the SUV range in a single season.',
  },
  {
    id: 'tv',
    code: 'TIGGO-V',
    model: 'Tiggo V',
    role: 'Lifestyle SUV',
    segment: 'SUV',
    units: 95,
    launch: 'Launches Q1 2027',
    intro:
      'The first new launch of 2027 — a lifestyle SUV that keeps the relaunch momentum alive into the new year and gives Marketing a fresh story one quarter after the national relaunch.',
  },
  {
    id: 't8',
    code: 'TIGGO-8',
    model: 'Tiggo 8',
    role: 'Flagship SUV',
    segment: 'SUV',
    units: 76,
    launch: 'Available 2027',
    intro:
      'The established flagship SUV bridging today’s range and the X-Series: seven seats, family-first, a proven nameplate that holds the premium ground until the X9 arrives.',
  },
  {
    id: 'him',
    code: 'HIMLA',
    model: 'Himla Pickup',
    role: 'Commercial fleet',
    segment: 'Pickup',
    units: 68,
    launch: 'Q4 2026 relaunch line',
    intro:
      'The commercial backbone: a pickup for government tenders, contractors and utility fleets. Small line, strategic reach — it opens fleet doors the passenger range then walks through.',
  },
  {
    id: 't7',
    code: 'TIGGO-7-PHEV',
    model: 'Tiggo 7 PHEV',
    role: 'Technology SUV',
    segment: 'PHEV',
    units: 48,
    launch: 'Q4 2026 relaunch line',
    intro:
      'The technology statement of the relaunch line: a plug-in hybrid SUV for eco and tech-minded buyers, proving the brand’s new-energy credentials from day one.',
  },
  {
    id: 'x9',
    code: 'TIGGO-X9',
    model: 'Tiggo X9',
    role: 'Premium flagship',
    segment: 'SUV',
    units: 43,
    launch: 'Launches Q4 2027',
    intro:
      'The premium flagship closes the year: the halo of the X-Series and the showcase piece of the expanded flagship showroom. Low volume, high signal.',
  },
  {
    id: 't9',
    code: 'TIGGO-9',
    model: 'Tiggo 9 PHEV',
    role: 'Brand halo',
    segment: 'PHEV',
    units: 27,
    launch: 'Q4 2026 relaunch line',
    intro:
      'The brand halo that leads the relaunch: the largest, most premium Chery in the lineup, there to reset what the market believes the badge can be — the rest of the range sells in its shadow.',
  },
];

export const CHANNEL_MIX = {
  title: 'Channel mix',
  channels: [
    { name: 'Retail', share: 68 },
    { name: 'Fleet', share: 32 },
  ],
  note: 'Fleet ≈ 420–490 units via government tenders, banks, telecom, rental, delivery and ride-hailing.',
} as const;

export interface Insight {
  stat: string;
  note: string;
}

export const INSIGHTS_TITLE = 'Market insights';

export const INSIGHTS: readonly Insight[] = [
  { stat: '35%', note: 'of the 2027 plan is electrified — EV, PHEV and HEV lines total 521 of 1,473 units; QQ3 EV alone is 1 in 5 sales' },
  { stat: '469', note: 'SUV units vs 415 sedans and 68 pickups — the X-Series wave lands in H2 2027 together with the flagship showroom' },
  {
    stat: '6 mo',
    note: 'typical buyer decision journey (~200 brand interactions) — each launch is staged a quarter ahead of the months it must sell in',
  },
  { stat: '600', note: 'units already ordered for 2026 against a 568-unit sales plan — early orders make availability itself a competitive weapon' },
];

export const RETAIL_NETWORK = {
  eyebrow: 'Retail network',
  title: 'Flagship showroom, Mecca Street',
  body: '2026 runs on the current showroom with sharper conversion and lead management. For 2027 the preferred path is expanding the current Mecca Street site (650+ sqm) — directly beside Mecca Mall and Plaza Outlets footfall — timed with the X-Series introduction, with three alternative sites (1,200–2,000 sqm) held as options.',
} as const;

export const ASKS = {
  title: 'Asks of Chery International',
  items: [
    'Production allocation',
    'Competitive pricing',
    'Marketing co-investment',
    'Fleet pricing support',
    'Early VIN allocation',
    'Training & after-sales support',
  ],
  note: 'Each ask is a shared success factor: allocation stabilizes cash-flow planning, pricing defends share against aggressive local rivals, VIN visibility gives customers firm delivery dates.',
} as const;
